import { useCallback, useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import AddressForm, {
  getAddressError,
  isAddressValid,
  useAddressState,
} from '@/components/AddressForm';
import { InputLocale } from '@/components/InputLocale/InputLocale';
import InputPhone, { isPhoneValid, usePhoneState } from '@/components/InputPhone/InputPhone';
import { SendcloudMap } from '@/components/SendcloudMap/SendcloudMap';
import { InputSelect } from '@/design_system/InputSelect/InputSelect';
import InputText from '@/design_system/InputText';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack';
import Toggle from '@/design_system/Toggle';
import { AddressRaw, formatAddress } from '@/models/address';
import { DeliveryOption } from '@/models/client';
import Phone from '@/models/partials/phone';
import { RequestWithRelations } from '@/models/request';
import { RequestContextData } from '@/routes/Requests/contexts/interfaces';
import { useGeoIpCountry } from '@/services/auth';
import { Locale } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';
import { isEmailValid } from '@/utils/email';
import { useBrandOrAppRequestContext } from '@/utils/useBrandOrAppRequestContext';
import useViewPort from '@/utils/useViewport';

import { TitleId, titleLabels, useComputeFullName } from './hooks/useComputeFullName';

import './ClientForm.css';

type TitleOption = { id: TitleId };

export const useClientState = (
  onChange?: (data: {
    name: string;
    title: string | null;
    firstName: string;
    lastName: string;
    vip: boolean;
    email: string;
    address: Partial<AddressRaw> | null;
    billingAddress: Partial<AddressRaw> | null;
    phone: Phone;
    deliveryOption: DeliveryOption;
    sendcloudServicePointId: string | null;
    locale: Locale;
  }) => void
) => {
  const { request } = useBrandOrAppRequestContext();
  const { computeFullName } = useComputeFullName();

  const [name, setName] = useState(request.client?.name ?? '');
  const [title, setTitle] = useState(request.client?.title ?? null);
  const [firstName, setFirstName] = useState(request.client?.firstName ?? '');
  const [lastName, setLastName] = useState(request.client?.lastName ?? '');
  const [vip, setVip] = useState(request.vip ?? false);
  const [email, setEmail] = useState(request.client?.email ?? '');
  const geoIpCountry = useGeoIpCountry();
  const [address, setAddress] = useAddressState(
    request.client?.address ?? { country: geoIpCountry }
  );
  const [sameAddress, setSameAddress] = useState(
    request.client?.address && request.client?.billingAddress
      ? formatAddress(request.client.address) === formatAddress(request.client.billingAddress)
      : true
  );
  const [billingAddress, setBillingAddress] = useAddressState(
    request.client?.billingAddress ?? { country: geoIpCountry }
  );
  const [phone, setPhone] = usePhoneState(request.client?.phone, request.store?.address?.country);
  const [deliveryOption, setDeliveryOption] = useState<DeliveryOption>(
    request.client?.deliveryOption ?? 'store'
  );
  const [sendcloudServicePointId, setSendcloudServicePointId] = useState<string | null>(
    request.client?.sendcloudServicePointId ?? null
  );
  const [locale, setLocale] = useState<Locale>(request.client?.locale ?? 'fr');

  const data = useMemo(
    () => ({
      name,
      title,
      firstName,
      lastName,
      vip,
      email,
      address,
      billingAddress,
      deliveryOption,
      sendcloudServicePointId,
      phone,
      locale,
    }),
    [
      address,
      billingAddress,
      deliveryOption,
      email,
      locale,
      name,
      title,
      firstName,
      lastName,
      phone,
      vip,
      sendcloudServicePointId,
    ]
  );

  // Required because AddressForm and InputPhone are slow to update
  const memoizedSetAddress = useCallback(
    (address: Partial<AddressRaw> | null) => {
      setAddress(address);

      if (sameAddress) {
        setBillingAddress(address);
      }

      onChange?.({ ...data, address, billingAddress: sameAddress ? address : billingAddress });
    },
    [billingAddress, data, onChange, sameAddress, setAddress, setBillingAddress]
  );

  const memoizedSetBillingAddress = useCallback(
    (address: Partial<AddressRaw> | null) => {
      setBillingAddress(address);
      onChange?.({ ...data, billingAddress: address });
    },
    [data, onChange, setBillingAddress]
  );

  const memoizedSetPhone = useCallback(
    (phone: Phone) => {
      setPhone(phone);
      onChange?.({ ...data, phone });
    },
    [data, onChange, setPhone]
  );

  return {
    name,
    setName: (name: string) => {
      setName(name);
      onChange?.({ ...data, name });
    },
    title,
    setTitle: (titleOption: TitleOption | null) => {
      const newFullName = computeFullName({
        titleId: titleOption?.id || null,
        firstName,
        lastName,
      });

      setTitle(titleOption?.id || null);
      setName(newFullName);
      onChange?.({
        ...data,
        title: titleOption?.id || null,
        name: newFullName,
      });
    },
    firstName,
    setFirstName: (firstName: string) => {
      const newFullName = computeFullName({
        titleId: title as TitleId | null,
        firstName,
        lastName,
      });

      setFirstName(firstName);
      setName(newFullName);
      onChange?.({
        ...data,
        firstName,
        name: newFullName,
      });
    },
    lastName,
    setLastName: (lastName: string) => {
      const newFullName = computeFullName({
        titleId: title as TitleId | null,
        firstName,
        lastName,
      });

      setLastName(lastName);
      setName(newFullName);
      onChange?.({
        ...data,
        lastName,
        name: newFullName,
      });
    },
    vip,
    setVip: (vip: boolean) => {
      setVip(vip);
      onChange?.({ ...data, vip });
    },
    email,
    setEmail: (email: string) => {
      setEmail(email);
      onChange?.({ ...data, email });
    },
    address,
    setAddress: memoizedSetAddress,
    sameAddress,
    setSameAddress: (sameAddress: boolean) => {
      setSameAddress(sameAddress);
      if (sameAddress) {
        setBillingAddress(address);
        onChange?.({ ...data, billingAddress: address });
      }
    },
    billingAddress,
    setBillingAddress: memoizedSetBillingAddress,
    deliveryOption,
    setDeliveryOption: (deliveryOption: DeliveryOption) => {
      setDeliveryOption(deliveryOption);
      setSendcloudServicePointId(null);
      onChange?.({ ...data, deliveryOption, sendcloudServicePointId: null });
    },
    sendcloudServicePointId,
    setSendcloudServicePointId: (sendcloudServicePointId: string | null) => {
      setSendcloudServicePointId(sendcloudServicePointId);
      onChange?.({ ...data, sendcloudServicePointId });
    },
    phone,
    setPhone: memoizedSetPhone,
    locale,
    setLocale: (locale: Locale) => {
      setLocale(locale);
      onChange?.({ ...data, locale });
    },
  };
};

export type ClientState = ReturnType<typeof useClientState>;

export const getClientError = ({
  client,
  view,
}: {
  client: ClientState | RequestWithRelations['client'];
  view: RequestContextData['view']['client'];
}) => {
  const nameError = view.required && !view.composedName.enabled && !client?.name;
  const titleError = view.required && view.composedName.enabled && !client?.title;
  const firstNameError = view.required && view.composedName.enabled && !client?.firstName;
  const lastNameError = view.required && view.composedName.enabled && !client?.lastName;
  // TODO: fix email validation. Wrong email is in error only if the phone has been considered as in error before
  const emailError =
    !!(view.required && !client?.email) || !!(client?.email && !isEmailValid(client.email));
  const addressError =
    !view.address.enabled ||
    (client?.deliveryOption === 'store' && !view.billingAddress.enabled) ||
    !view.required
      ? false
      : !isAddressValid(client?.address, false);
  const billingAddressError =
    !view.billingAddress.enabled || !view.required || !client?.billingAddress
      ? false
      : !isAddressValid(client.billingAddress, false);
  const phoneError = !isPhoneValid(client?.phone, !view.required);

  return {
    nameError,
    titleError,
    firstNameError,
    lastNameError,
    emailError,
    addressError,
    billingAddressError,
    phoneError,
    hasError:
      nameError ||
      titleError ||
      firstNameError ||
      lastNameError ||
      emailError ||
      addressError ||
      billingAddressError ||
      phoneError,
  };
};

export type ClientFormProps = {
  state: ClientState;
  size?: 'medium' | 'large';
  variant?: 'default' | 'brand';
  disableEmailEdit?: boolean;
  hideVip?: boolean;
  hideLanguage?: boolean;
  hideDeliveryOption?: boolean;
  showErrors?: boolean;
};

const { block, element } = createBEMClasses('client-form');

const ClientForm = ({
  state,
  size = 'medium',
  variant = 'default',
  disableEmailEdit = false,
  hideVip = false,
  hideLanguage = false,
  hideDeliveryOption = false,
  showErrors = false,
}: ClientFormProps) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const { request, workflow, view } = useBrandOrAppRequestContext();

  const {
    name,
    setName,
    title,
    setTitle,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    vip,
    setVip,
    email,
    setEmail,
    address,
    setAddress,
    sameAddress,
    setSameAddress,
    billingAddress,
    setBillingAddress,
    deliveryOption,
    setDeliveryOption,
    sendcloudServicePointId,
    setSendcloudServicePointId,
    phone,
    setPhone,
    locale,
    setLocale,
  } = state;

  const {
    nameError,
    titleError,
    firstNameError,
    lastNameError,
    emailError,
    addressError,
    billingAddressError,
    phoneError,
  } = getClientError({
    client: state,
    view: view.client,
  });

  const allowedDeliveryOptions = workflow?.config.deliveryOptions ?? [];

  const deliveryOptions = [
    allowedDeliveryOptions.includes('store') &&
      !!request.storeId && {
        id: 'store',
        text: t({
          id: 'components.client-form.delivery-option.store.label',
          message: 'In store',
        }),
      },
    allowedDeliveryOptions.includes('home') && {
      id: 'home',
      text: t({
        id: 'components.client-form.delivery-option.home.label',
        message: 'At home',
      }),
    },
    allowedDeliveryOptions.includes('pickupPoint_mondialRelay') && {
      id: 'pickupPoint_mondialRelay',
      text: t({
        id: 'components.client-form.delivery-option.pickup-point-mondial-relay.label',
        message: 'Pickup point (Mondial Relay)',
      }),
    },
  ].filter((deliveryOption) => !!deliveryOption);

  const hideDeliveryOptionSelect = hideDeliveryOption || deliveryOptions.length <= 1;

  const canEditVip = request.articles
    .filter((article) => !article.archivedAt)
    .every(
      (article) =>
        article.step?.step === 'creation' ||
        article.taskType === 'choose_article_service' ||
        article.taskType === 'accept_dispatch_proposal' ||
        article.taskType === 'accept_requalification'
    );

  const dataFromCrm = request.client?.dataFromCrm;

  return (
    <Stack gap="1rem" className={block()}>
      <Stack gap="4px">
        {view.client.composedName.enabled ? (
          <div className={element('composed-name')}>
            <Title
              title={title}
              setTitle={setTitle}
              titleError={titleError}
              size={size}
              showErrors={showErrors}
            />
            <ClientNameInput
              label={t({ id: 'components.client-form.first-name.label', message: 'First name' })}
              type="firstName"
              value={firstName}
              setValue={setFirstName}
              valueError={firstNameError}
              size={size}
              showErrors={showErrors}
            />
            <ClientNameInput
              label={t({ id: 'components.client-form.last-name.label', message: 'Last name' })}
              type="lastName"
              value={lastName}
              setValue={setLastName}
              valueError={lastNameError}
              size={size}
              showErrors={showErrors}
            />
          </div>
        ) : (
          <ClientNameInput
            label={t({ id: 'components.client-form.name.label', message: 'Full name' })}
            type="fullName"
            value={name}
            setValue={setName}
            valueError={nameError}
            size={size}
            showErrors={showErrors}
          />
        )}

        {view.client.vip.enabled && !hideVip && (
          <div style={{ display: 'flex' }}>
            <Toggle
              variant={variant}
              size="large"
              alignment="right"
              label={t({ id: 'components.client-form.vip.label', message: 'This is a VIP client' })}
              isSelected={vip}
              onChange={setVip}
              isDisabled={!canEditVip}
            />
          </div>
        )}
      </Stack>
      <InputText
        label={
          <>
            <Trans id="components.client-form.email.label">Email</Trans>
            {!view.client.required && <OptionalLabel />}
          </>
        }
        placeholder={t({
          id: 'components.client-form.email.placeholder',
          message: 'i.e: jane.doe@mail.com',
        })}
        size={size}
        value={email}
        onChange={setEmail}
        type="email"
        isDisabled={disableEmailEdit || !!dataFromCrm?.email}
        error={
          showErrors && emailError
            ? email
              ? t({
                  id: 'components.client-form.email-invalid.error',
                  message: 'Please add a valid email',
                })
              : t({
                  id: 'components.client-form.email.error',
                  message: 'Please add an email',
                })
            : undefined
        }
      />
      <InputPhone
        label={
          <>
            <Trans id="components.client-form.phone.label">Phone</Trans>
            {!view.client.required && <OptionalLabel />}
          </>
        }
        value={phone}
        onChange={setPhone}
        defaultCountry={request.store?.address?.country}
        variant={variant}
        size={size}
        isInvalid={showErrors ? phoneError : undefined}
        isDisabled={!!dataFromCrm?.phone}
      />
      {view.client.address.enabled && (
        <Stack gap="4px">
          <AddressForm
            label={
              <>
                <Trans id="components.client-form.address.label">Address</Trans>
                {(!view.client.required ||
                  (deliveryOption === 'store' && !view.client.billingAddress.enabled)) && (
                  <OptionalLabel />
                )}
              </>
            }
            value={address}
            onChange={setAddress}
            contact={false}
            variant={variant}
            size={size}
            disabledFields={{
              line1: !!dataFromCrm?.address?.line1,
              line2: !!dataFromCrm?.address?.line2,
              zipCode: !!dataFromCrm?.address?.zipCode,
              city: !!dataFromCrm?.address?.city,
              state: !!dataFromCrm?.address?.state,
              country: !!dataFromCrm?.address?.country,
            }}
            error={
              showErrors && addressError && view.client.required
                ? getAddressError(address, false)
                : undefined
            }
          />
          {view.client.billingAddress.enabled && (
            <div style={{ display: 'flex' }}>
              <Toggle
                variant={variant}
                size="large"
                alignment="right"
                label={t({
                  id: 'components.client-form.same-address.label',
                  message: 'Use the same address for billing',
                })}
                isSelected={sameAddress}
                onChange={setSameAddress}
              />
            </div>
          )}
        </Stack>
      )}
      {!sameAddress && view.client.billingAddress.enabled && (
        <AddressForm
          label={
            <>
              {t({
                id: 'components.client-form.billing-address.label',
                message: 'Billing address',
              })}
              {!view.client.required && <OptionalLabel />}
            </>
          }
          value={billingAddress}
          onChange={setBillingAddress}
          contact={false}
          variant={variant}
          size={size}
          error={
            showErrors && billingAddressError && view.client.required
              ? getAddressError(billingAddress, false)
              : undefined
          }
        />
      )}
      {(!hideDeliveryOptionSelect || !hideLanguage) && (
        <Stack row={!isMobile} gap="1rem">
          {!hideDeliveryOptionSelect && (
            <InputSelect
              variant="select"
              label={t({
                id: 'components.client-form.delivery-option.label',
                message: 'Shipping back preference',
              })}
              tooltip={t({
                id: 'components.client-form.delivery-option.tooltip',
                message: 'Place where the client would like to receive back their items',
              })}
              tooltipPlacement="top"
              isSearchable={false}
              value={deliveryOptions.find(
                (shippingBackOption) => shippingBackOption.id === deliveryOption
              )}
              options={deliveryOptions}
              getOptionValue={(deliveryOption) => deliveryOption.id}
              getOptionLabel={(deliveryOption) => deliveryOption.text}
              onChange={(deliveryOption) => {
                if (deliveryOption) {
                  setDeliveryOption(deliveryOption.id as DeliveryOption);
                }
              }}
              styleVariant={variant}
              size={size}
              style={{ flex: 1 }}
            />
          )}

          {!hideLanguage && (
            <InputLocale
              value={locale}
              onChange={setLocale}
              styleVariant={variant}
              size={size}
              style={{ flex: 1 }}
              menuPlacement="top"
              isDisabled={!!dataFromCrm?.locale}
            />
          )}
        </Stack>
      )}

      {deliveryOption === 'pickupPoint_mondialRelay' && (
        <SendcloudMap
          servicePointId={sendcloudServicePointId}
          setServicePointId={setSendcloudServicePointId}
          carrier="mondial_relay"
          address={address?.line1}
          postalCode={address?.zipCode}
          city={address?.city}
          country={address?.country ?? undefined}
        />
      )}
    </Stack>
  );
};

const Title = ({
  title,
  setTitle,
  titleError,
  size = 'medium',
  showErrors = false,
}: {
  title: string | null;
  setTitle: (titleOption: TitleOption | null) => void;
  titleError: boolean;
  size?: 'medium' | 'large';
  showErrors?: boolean;
}) => {
  const { i18n, t } = useLingui();
  const { request, view } = useBrandOrAppRequestContext();

  const titleOptions: { id: TitleId }[] = [
    {
      id: 'ms',
    },
    {
      id: 'mr',
    },
    {
      id: 'unspecified',
    },
  ];

  return (
    <InputSelect
      variant="select"
      label={
        <>
          <Trans id="components.client-form.title.label">Title</Trans>
          {!view.client.required && <OptionalLabel />}
        </>
      }
      placeholder={t({
        id: 'components.client-form.title.placeholder',
        message: 'Title',
      })}
      isSearchable={false}
      isClearable
      value={titleOptions.find((titleOption) => titleOption.id === title)}
      options={titleOptions}
      getOptionValue={(titleOption) => titleOption.id}
      getOptionLabel={(titleOption) => i18n._(titleLabels[titleOption.id])}
      onChange={(titleOption, action) => {
        if (action.action === 'clear') {
          setTitle(null);
        } else if (titleOption) {
          setTitle(titleOption);
        }
      }}
      isDisabled={!view.client.name.editable || !!request.client?.dataFromCrm?.title}
      error={
        showErrors && titleError
          ? t({
              id: 'components.client-form.title.error',
              message: 'Please add a title',
            })
          : undefined
      }
      size={size}
      style={{ flex: '0 1 auto', minWidth: '8.75rem' }}
    />
  );
};

const ClientNameInput = ({
  label,
  type,
  value,
  setValue,
  valueError,
  size = 'medium',
  showErrors = false,
}: {
  label: string;
  type: 'fullName' | 'firstName' | 'lastName';
  value: string;
  setValue: (value: string) => void;
  valueError: boolean;
  size?: 'medium' | 'large';
  showErrors?: boolean;
}) => {
  const { t } = useLingui();
  const { request, view } = useBrandOrAppRequestContext();

  const labels = {
    placeholder: t({
      id: 'components.client-form.name.placeholder',
      message: 'i.e: Jane Doe',
    }),
    error: t({
      id: 'components.client-form.name.error',
      message: 'Please add a full name',
    }),
  };

  switch (type) {
    case 'firstName':
      labels.placeholder = t({
        id: 'components.client-form.first-name.placeholder',
        message: 'i.e: Jane',
      });
      labels.error = t({
        id: 'components.client-form.first-name.error',
        message: 'Please add a first name',
      });
      break;
    case 'lastName':
      labels.placeholder = t({
        id: 'components.client-form.last-name.placeholder',
        message: 'i.e: Doe',
      });
      labels.error = t({
        id: 'components.client-form.last-name.error',
        message: 'Please add a last name',
      });
      break;
    default:
      break;
  }

  const dataFromCrm = request.client?.dataFromCrm;
  const isLockedFromCrm =
    (type === 'fullName' && !!dataFromCrm?.name) ||
    (type === 'firstName' && !!dataFromCrm?.firstName) ||
    (type === 'lastName' && !!dataFromCrm?.lastName);

  return (
    <InputText
      label={
        <>
          {label}
          {!view.client.required && <OptionalLabel />}
        </>
      }
      placeholder={labels.placeholder}
      size={size}
      value={value}
      onChange={(value) => {
        setValue(value);
      }}
      isDisabled={!view.client.name.editable || isLockedFromCrm}
      error={showErrors && valueError ? labels.error : undefined}
      style={{ flex: 1 }}
    />
  );
};

export default ClientForm;
